/**
 * ScriptRecipeStore — injected metadata backend for the script flow.
 *
 * The SDK is standalone (no DB dependency). The backend implements this
 * interface over Postgres (full-text search + atomic counters) and injects it
 * via `collections['script-recipes']`, exactly like `collections['source-embeddings']`.
 * `ScriptStore` consumes it for all METADATA operations while keeping the
 * executable body on disk as scripts-store/<fileBase>.ts.
 *
 * All metadata rows are plain JSON (no scriptBody — that lives on disk).
 * See backend/docs/SCRIPT-FLOW-SCALING-ISSUES.md (#1, #3, #7).
 */

import type { ScriptParameter, ScriptComponentSpec } from './types';

/** One recipe's metadata as stored in Postgres (mirrors the script_recipes table). */
export interface ScriptRecipeMetaRow {
	id: string;
	projectId?: string | null;
	version: number;
	name: string;
	intentDescription: string;
	tags: string[] | null;
	createdFrom: string | null;
	sourceIds: string[] | null;
	tables: string[] | null;
	parameters: ScriptParameter[] | null;
	components?: ScriptComponentSpec[] | null;
	fileBase: string;
	bodyHash?: string | null;
	successCount: number;
	failureCount: number;
	lastUsed: string | null;
	parentId?: string | null;
	forkDepth?: number | null;
	forkReason?: string | null;
	status: 'draft' | 'verified' | string;
	turnId?: string | null;
	lastError?: {
		phase: 'compile' | 'runtime' | 'timeout' | 'ipc';
		message: string;
		at: string;
		attempt: number;
	} | null;
	createdAt?: string | null;
	updatedAt?: string | null;
}

export interface ScriptRecipeStore {
	/** FTS shortlist of healthy verified recipes for the matcher (metadata only). */
	search(params: { prompt: string; projectId?: string; limit?: number }): Promise<ScriptRecipeMetaRow[]>;
	/** Fetch one recipe by id (any status). */
	getById(id: string): Promise<ScriptRecipeMetaRow | null>;
	/** Count healthy verified recipes (drives the "any scripts?" gate). */
	count(params?: { projectId?: string }): Promise<number>;
	/** Insert or update a recipe row (keyed by id). */
	upsert(row: ScriptRecipeMetaRow): Promise<void>;
	/** Atomically bump counters / last-used. */
	updateStats(id: string, patch: { successDelta?: number; failureDelta?: number; lastUsed?: string }): Promise<void>;
	/** Flip a draft to verified, applying provenance + optional fork lineage. */
	promote(id: string, patch: {
		sourceIds: string[];
		tables: string[];
		fileBase?: string;
		parentId?: string;
		forkDepth?: number;
		forkReason?: string;
		components?: ScriptComponentSpec[];
	}): Promise<ScriptRecipeMetaRow | null>;
	/** Stamp a draft's last execution error. */
	recordDraftError(id: string, err: { phase: string; message: string; attempt: number; at: string }): Promise<void>;
	/** Delete a recipe row (body file removed separately). */
	remove(id: string): Promise<void>;
	/** True if `fileBase` is taken by a different recipe in this project. */
	fileBaseTaken(fileBase: string, excludeId: string, projectId?: string): Promise<boolean>;
}

/** Pull the injected store off the collections bag (or null if not wired). */
export function resolveScriptRecipeStore(collections: any): ScriptRecipeStore | null {
	const s = collections?.['script-recipes'];
	if (s && typeof s.search === 'function' && typeof s.getById === 'function') {
		return s as ScriptRecipeStore;
	}
	return null;
}
